import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppSelector, useAppDispatch } from "store";
import { removeSnackbar } from "store/commonSlice";
import Snackbar from "./Snackbar";
import { SnackbarConfig, DEFAULT_CONFIG, SnackbarType as LocalSnackbarType } from "./types";
import "./styles.css";

/** Published on <html> so other bottom-corner floaters (the Miro jobs tray) can sit above the stack only while it is on screen. */
export const SNACKBAR_STACK_OFFSET_VAR = "--snackbar-stack-offset";
const STACK_GAP_PX = 10;

interface SnackbarProviderProps {
  config?: Partial<SnackbarConfig>;
}

const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ config = {} }) => {
  const dispatch = useAppDispatch();
  const snackbars = useAppSelector((state) => state.common.snackbar);

  const mergedConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);

  const handleDismiss = useCallback(
    (id: string) => {
      dispatch(removeSnackbar(id));
    },
    [dispatch]
  );

  const positionClasses = useMemo(() => {
    const positions = {
      "top-left": "top-5 left-5 items-start",
      "top-center": "top-5 left-1/2 -translate-x-1/2 items-center",
      "top-right": "top-5 right-5 items-end",
      "bottom-left": "bottom-5 left-5 items-start",
      "bottom-center": "bottom-5 left-1/2 -translate-x-1/2 items-center",
      // Lifted above a bottom-pinned bar while one is on screen (useSnackbarBottomClearance, REF-7075).
      "bottom-right": "bottom-[calc(1.25rem+var(--snackbar-bottom-clearance,0px))] right-5 items-end",
    };

    return positions[mergedConfig.position || "bottom-right"];
  }, [mergedConfig.position]);

  const stackDirection = mergedConfig.stackDirection === "down" ? "flex-col-reverse" : "flex-col";

  // Publish the stack's REAL height (0 when empty) as a CSS var, so a floater that shares the bottom-right corner moves up only while a
  // snackbar is visible and falls back as it leaves (the height follows the exit animation too). Only the bottom-right stack matters.
  const stackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stackRef.current;
    const root = document.documentElement;
    if (!el || (mergedConfig.position || "bottom-right") !== "bottom-right") return;

    const publish = () => {
      const height = el.getBoundingClientRect().height;
      root.style.setProperty(SNACKBAR_STACK_OFFSET_VAR, height > 0 ? `${Math.ceil(height) + STACK_GAP_PX}px` : "0px");
    };
    publish();
    if (typeof ResizeObserver === "undefined") return () => root.style.removeProperty(SNACKBAR_STACK_OFFSET_VAR);

    const observer = new ResizeObserver(publish);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.removeProperty(SNACKBAR_STACK_OFFSET_VAR);
    };
  }, [mergedConfig.position]);

  const visibleSnackbars = useMemo(() => snackbars.slice(0, mergedConfig.maxSnackbars), [snackbars, mergedConfig.maxSnackbars]);

  return (
    <div ref={stackRef} className={`fixed flex ${stackDirection} gap-2.5 z-[999999] pointer-events-none ${positionClasses}`} role="region" aria-label="Notifications" aria-live="polite">
      {visibleSnackbars.map((item) => {
        const snackbarData = {
          ...item,
          type: (item.type ?? LocalSnackbarType.Success) as LocalSnackbarType,
        };

        return (
          <div key={item.id} className="pointer-events-auto">
            <Snackbar data={snackbarData} onDismiss={handleDismiss} />
          </div>
        );
      })}
    </div>
  );
};

export default SnackbarProvider;
